import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { api } from "../lib/api";

/**
 * Iter59.p24 — Theme management.
 *
 * Modes:
 *   - "light"    → explicit light
 *   - "dark"     → explicit dark
 *   - "comfort"  → sepia / eye-comfort
 *   - "system"   → follows prefers-color-scheme (light | dark)
 *
 * State model: `mode` is what the user picked; `resolvedTheme` is the
 * "light|dark|comfort" that's actually applied to <html data-theme>.
 * `customTokens` is an optional map of CSS variables to inline on
 * <html> for advanced overrides (e.g. custom --chart-series-1).
 *
 * Persistence:
 *   1. Optimistic: localStorage("asl_theme") is read pre-React by an
 *      inline script in index.html, so first paint is correct.
 *   2. Canonical: `/api/me/preferences` returns { mode, custom_tokens }
 *      after login. If it differs from localStorage, we apply the
 *      canonical version and cache it locally.
 *   3. On change, we PATCH /api/me/preferences and write to
 *      localStorage.
 */

const STORAGE_KEY = "asl_theme";
export const THEME_MODES = ["light", "dark", "comfort", "system"];

function _prefersDark() {
    if (typeof window === "undefined") return false;
    return !!(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
}

function _resolve(mode) {
    if (mode === "system") return _prefersDark() ? "dark" : "light";
    if (THEME_MODES.includes(mode) && mode !== "system") return mode;
    return "light";
}

function _readInitial() {
    // Match the inline script's storage read.
    try {
        if (typeof window === "undefined") return { mode: "system", customTokens: {} };
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return { mode: "system", customTokens: {} };
        const parsed = JSON.parse(raw);
        return {
            mode: THEME_MODES.includes(parsed?.mode) ? parsed.mode : "system",
            customTokens: (parsed && typeof parsed.custom_tokens === "object" && parsed.custom_tokens)
                || (parsed && typeof parsed.customTokens === "object" && parsed.customTokens)
                || {},
        };
    } catch {
        return { mode: "system", customTokens: {} };
    }
}

function _applyDom(mode, customTokens) {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const resolved = _resolve(mode);
    root.setAttribute("data-theme", resolved);
    // Clear any previously-applied inline overrides.
    const prev = root.dataset.aslCustomKeys ? root.dataset.aslCustomKeys.split(",") : [];
    prev.forEach((k) => { if (k) root.style.removeProperty(k); });
    const applied = [];
    for (const [k, v] of Object.entries(customTokens || {})) {
        if (typeof k === "string" && k.startsWith("--") && typeof v === "string") {
            root.style.setProperty(k, v);
            applied.push(k);
        }
    }
    root.dataset.aslCustomKeys = applied.join(",");
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("asl:theme", {
            detail: { mode, resolved, customTokens },
        }));
    }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
    const initial = useMemo(_readInitial, []);
    const [mode, setMode] = useState(initial.mode);
    const [customTokens, setCustomTokens] = useState(initial.customTokens);
    const [resolvedTheme, setResolvedTheme] = useState(() => _resolve(initial.mode));
    const bootstrappedRef = useRef(false);

    // Apply to DOM whenever mode or tokens change.
    useEffect(() => {
        _applyDom(mode, customTokens);
        setResolvedTheme(_resolve(mode));
    }, [mode, customTokens]);

    // Re-resolve if the OS switches themes while "system" is selected.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        if (mode !== "system") return undefined;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const listener = () => {
            _applyDom("system", customTokens);
            setResolvedTheme(_resolve("system"));
        };
        if (mq.addEventListener) mq.addEventListener("change", listener);
        else if (mq.addListener) mq.addListener(listener);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", listener);
            else if (mq.removeListener) mq.removeListener(listener);
        };
    }, [mode, customTokens]);

    // On mount, fetch canonical preferences from the server. If it
    // differs from our local snapshot, apply + cache.
    useEffect(() => {
        if (bootstrappedRef.current) return;
        bootstrappedRef.current = true;
        (async () => {
            try {
                const { data } = await api.get("/me/preferences");
                if (!data) return;
                const serverMode = THEME_MODES.includes(data.mode) ? data.mode : "system";
                const serverTokens = (data.custom_tokens && typeof data.custom_tokens === "object")
                    ? data.custom_tokens : {};
                setMode(serverMode);
                setCustomTokens(serverTokens);
                try {
                    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
                        mode: serverMode, custom_tokens: serverTokens,
                    }));
                } catch { /* storage blocked */ }
            } catch {
                /* not authenticated yet — the login screen will render
                   with the initial (localStorage) theme, which is fine. */
            }
        })();
    }, []);

    // API surface.
    const persist = useCallback(async (nextMode, nextTokens) => {
        // Optimistic local + storage first.
        setMode(nextMode);
        setCustomTokens(nextTokens || {});
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
                mode: nextMode, custom_tokens: nextTokens || {},
            }));
        } catch { /* ignore */ }
        // Best-effort persist to server. If we're not signed in, the
        // request will 401 — that's fine, the login flow will rehydrate.
        try {
            await api.patch("/me/preferences", {
                mode: nextMode,
                custom_tokens: nextTokens || {},
            });
        } catch {
            /* ignore */
        }
    }, []);

    const setModeAndPersist = useCallback((next) => persist(next, customTokens), [customTokens, persist]);
    const setCustomTokensAndPersist = useCallback((next) => persist(mode, next), [mode, persist]);
    const previewOnly = useCallback((next) => {
        // Apply without persisting (used by the Theme Management live
        // preview until the user clicks Save).
        setMode(next.mode || mode);
        setCustomTokens(next.customTokens || {});
    }, [mode]);

    const value = useMemo(() => ({
        mode,
        resolvedTheme,
        customTokens,
        setMode: setModeAndPersist,
        setCustomTokens: setCustomTokensAndPersist,
        previewOnly,
        // Reset removes any custom tokens and switches back to system.
        reset: () => persist("system", {}),
    }), [mode, resolvedTheme, customTokens, setModeAndPersist, setCustomTokensAndPersist, previewOnly, persist]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
    return ctx;
}
